import React, { useState, useEffect } from 'react';
import { 
  subscribeToUserCollection, 
  createProductionBatch, 
  updateUserDoc, 
  deleteUserDoc 
} from '../utils/userDataHelper';
import Breadcrumbs from '../Components/Breadcrumbs';
import Footer from '../Components/Footer';
import './labcss/batches-pages.css';

// Import centralized utilities
import { num } from './utils/costCalculations';
import { normalizeRecipe } from '../utils/recipeSchema';
import { getBatchReadyDate, getBatchCureDays } from '../utils/workflowPlanning';

const BATCH_STATUSES = [
  { id: 'planned', label: 'Planned', color: '#9e9e9e' },
  { id: 'in_progress', label: 'In Progress', color: '#2196f3' },
  { id: 'curing', label: 'Curing', color: '#ff9800' },
  { id: 'testing', label: 'Testing', color: '#9c27b0' },
  { id: 'ready', label: 'Ready', color: '#4caf50' },
  { id: 'sold', label: 'Sold', color: '#795548' },
];

// Single Batch Card Component
const BatchCard = ({ batch, recipes, onUpdate, onDelete, onAddNote }) => {
  const [expanded, setExpanded] = useState(false);
  const [editingStatus, setEditingStatus] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [showQC, setShowQC] = useState(false);
  const [qcData, setQcData] = useState(batch.qc || {});

  const recipe = batch.recipeSnapshot || recipes.find(r => r.id === batch.recipeId);
  const status = BATCH_STATUSES.find(s => s.id === batch.status) || BATCH_STATUSES[0];
  
  const madeDate = batch.madeDate ? new Date(batch.madeDate) : null;
  const readyDate = getBatchReadyDate(batch, recipe);
  const today = new Date();
  const daysRemaining = readyDate ? Math.ceil((readyDate - today) / (1000 * 60 * 60 * 24)) : null;
  const cureProgress = madeDate ? Math.min(100, Math.max(0, ((today - madeDate) / (Math.max(1, getBatchCureDays(batch, recipe)) * 24 * 60 * 60 * 1000)) * 100)) : 0;

  const handleStatusChange = async (newStatus) => {
    if (await onUpdate(batch.id, { status: newStatus })) setEditingStatus(false);
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    const notes = Array.isArray(batch.notes) ? [...batch.notes] : batch.notes ? [{ text: batch.notes, date: batch.madeDate }] : [];
    notes.push({
      text: newNote,
      date: new Date().toISOString(),
    });
    if (await onUpdate(batch.id, { notes })) setNewNote('');
  };

  const handleQCSave = async () => {
    if (await onUpdate(batch.id, { qc: qcData, status: 'ready' })) setShowQC(false);
  };

  return (
    <div className="bat-card">
      {/* Header */}
      <div className="bat-card-header" onClick={() => setExpanded(!expanded)}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <span className="bat-card-title">{batch.name || 'Unnamed Batch'}</span>
            <span className="bat-card-badge" style={{ background: status.color }}>
              {status.label}
            </span>
          </div>
          <div className="bat-card-subtitle">
            {recipe?.name || batch.recipeName || 'Unknown recipe'} • {batch.bars || '?'} bars {batch.remainingBars !== undefined ? `(${batch.remainingBars} remaining)` : '(legacy stock unverified)'}
            {madeDate && ` • Made: ${madeDate.toLocaleDateString()}`}
          </div>
        </div>

        {/* Cure Progress (if curing) */}
        {batch.status === 'curing' && madeDate && (
          <div className="bat-cure-mini">
            <div className="days">
              {daysRemaining > 0 ? `${daysRemaining} days left` : 'Ready to test!'}
            </div>
            <div className="bat-progress-bar">
              <div className="fill" style={{ width: `${cureProgress}%`, background: status.color }} />
            </div>
          </div>
        )}

        <span className={`bat-card-arrow${expanded ? ' open' : ''}`}>▼</span>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="bat-card-body">
          {/* Status Change */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontWeight: 600, fontSize: '0.9em', marginBottom: 8, display: 'block' }}>Status:</label>
            {editingStatus ? (
              <div className="bat-status-pills">
                {BATCH_STATUSES.map(s => (
                  <button
                    key={s.id}
                    className="bat-status-pill"
                    onClick={() => handleStatusChange(s.id)}
                    style={{
                      background: s.id === batch.status ? s.color : '#f8fafc',
                      color: s.id === batch.status ? '#fff' : '#0f172a',
                      borderColor: s.color,
                    }}
                  >
                    {s.label}
                  </button>
                ))}
                <button className="bat-status-pill" style={{ borderColor: '#e2e8f0' }} onClick={() => setEditingStatus(false)}>Cancel</button>
              </div>
            ) : (
              <button 
                className="bat-action-btn"
                onClick={() => setEditingStatus(true)}
                style={{ background: status.color }}
              >
                {status.label} — Change
              </button>
            )}
          </div>

          {/* Batch Details */}
          <div className="bat-detail-grid" style={{ marginBottom: 16 }}>
            <div className="bat-detail-item">
              <div className="label">Bars</div>
              <div className="value">{batch.bars || '—'}</div>
            </div>
            <div className="bat-detail-item">
              <div className="label">Batch Mass</div>
              <div className="value">{num(batch.batchMass, 0).toFixed(0)}g</div>
            </div>
            <div className="bat-detail-item">
              <div className="label">Cost/Bar</div>
              <div className="value">₹{num(batch.costPerBar, 0).toFixed(2)}</div>
            </div>
            <div className="bat-detail-item">
              <div className="label">Ready Date</div>
              <div className="value">{readyDate ? readyDate.toLocaleDateString() : '—'}</div>
            </div>
            <div className="bat-detail-item" style={{ background: batch.soapExpiryDate && new Date(batch.soapExpiryDate) < new Date(Date.now() + 90 * 24 * 60 * 60 * 1000) ? '#fffbeb' : undefined }}>
              <div className="label">Soap Expiry</div>
              <div className="value" style={{ color: batch.soapExpiryDate && new Date(batch.soapExpiryDate) < today ? '#dc2626' : undefined }}>
                {batch.soapExpiryDate ? new Date(batch.soapExpiryDate).toLocaleDateString() : '—'}
              </div>
            </div>
          </div>

          {/* Curing Progress Bar (detailed) */}
          {batch.status === 'curing' && madeDate && (
            <div className="bat-cure-detail" style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontWeight: 600 }}>Curing Progress</span>
                <span>{cureProgress.toFixed(0)}%</span>
              </div>
              <div className="progress-big">
                <div className="fill" style={{ width: `${cureProgress}%` }} />
              </div>
              <div className="bat-cure-weeks">
                <span>Week 1</span>
                <span>Week 2</span>
                <span>Week 3</span>
                <span>Week 4</span>
                <span>Week 5</span>
                <span>Week 6</span>
              </div>
            </div>
          )}

          {/* Quality Control */}
          {(batch.status === 'testing' || batch.status === 'curing') && (
            <div style={{ marginBottom: 16 }}>
              <button 
                className="bat-action-btn secondary"
                onClick={() => setShowQC(!showQC)}
              >
                {showQC ? 'Hide' : 'Run'} Quality Control Test
              </button>
              
              {showQC && (
                <div className="bat-qc-panel">
                  <div className="bat-qc-grid">
                    <div>
                      <label>pH Level</label>
                      <input 
                        type="number" 
                        step="0.1"
                        value={qcData.ph || ''} 
                        onChange={e => setQcData({...qcData, ph: e.target.value})}
                        placeholder="8-10 ideal"
                      />
                    </div>
                    <div>
                      <label>Hardness (1-5)</label>
                      <input 
                        type="number" 
                        min="1" max="5"
                        value={qcData.hardness || ''} 
                        onChange={e => setQcData({...qcData, hardness: e.target.value})}
                      />
                    </div>
                    <div>
                      <label>Lather (1-5)</label>
                      <input 
                        type="number" 
                        min="1" max="5"
                        value={qcData.lather || ''} 
                        onChange={e => setQcData({...qcData, lather: e.target.value})}
                      />
                    </div>
                    <div>
                      <label>Scent (1-5)</label>
                      <input 
                        type="number" 
                        min="1" max="5"
                        value={qcData.scent || ''} 
                        onChange={e => setQcData({...qcData, scent: e.target.value})}
                      />
                    </div>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input 
                        type="checkbox" 
                        checked={qcData.zapTest === 'pass'} 
                        onChange={e => setQcData({...qcData, zapTest: e.target.checked ? 'pass' : 'fail'})}
                      />
                      <span>Quality checks completed using your documented testing process</span>
                    </label>
                  </div>
                  <button 
                    className="bat-action-btn primary"
                    onClick={handleQCSave}
                  >
                    Save QC & Mark Ready
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Notes / Journal */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 600, fontSize: '0.9em', marginBottom: 8 }}>Notes & Journal</div>
            
            {typeof batch.notes === 'string' && <p>{batch.notes}</p>}
            {/* Existing notes */}
            {Array.isArray(batch.notes) && batch.notes.length > 0 && (
              <div className="bat-notes-list">
                {batch.notes.map((note, i) => (
                  <div key={i} className="bat-note-item">
                    <div className="date">
                      {new Date(note.date).toLocaleString()}
                    </div>
                    <div>{note.text}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Add note */}
            <div className="bat-note-input">
              <input
                type="text"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Add a note (observations, changes, etc.)"
                onKeyPress={(e) => e.key === 'Enter' && handleAddNote()}
              />
              <button 
                className="bat-action-btn primary"
                onClick={handleAddNote}
              >
                Add
              </button>
            </div>
          </div>

          <p className="bodytext" style={{ marginBottom: 12 }}>
            {batch.inventoryApplied === true ? `Ingredients deducted. Packaging ${batch.packagingTracked ? 'deducted' : 'not linked'}.` : batch.inventoryStatus === 'record_only' ? 'Record only: ingredient and packaging stock were not changed.' : 'Legacy batch: ingredient consumption and remaining stock are unverified.'}
          </p>
          {/* Actions */}
          <div className="bat-actions">
            <a 
              className="bat-action-btn secondary"
              href="/print" 
              onClick={() => sessionStorage.setItem('printBatch', JSON.stringify(batch))}
            >
              Print
            </a>
            <button 
              className="bat-action-btn danger"
              onClick={() => { if (window.confirm(batch.inventoryApplied === true ? 'Delete this unsold batch and return its recorded ingredient and packaging quantities to inventory? Linked sales must be removed first.' : 'Delete this batch record? No ingredient or packaging stock will be changed.')) onDelete(batch.id); }}
            >
              Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Create Batch Modal
const CreateBatchModal = ({ recipes, packagingItems, onClose, onCreate }) => {
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [batchName, setBatchName] = useState('');
  const [madeDate, setMadeDate] = useState(new Date().toISOString().split('T')[0]);
  const [bars, setBars] = useState(10);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [packagingId, setPackagingId] = useState('');
  const [deductStock, setDeductStock] = useState(true);
  const [soapExpiryMonths, setSoapExpiryMonths] = useState(12); // Default 12 months shelf life

  const handleCreate = async () => {
    if (!selectedRecipe) {
      alert('Please select a recipe');
      return;
    }

    // Calculate expiry date for finished soap
    const madeD = new Date(madeDate);
    const readyD = new Date(madeD.getTime() + getBatchCureDays({}, selectedRecipe) * 24 * 60 * 60 * 1000);
    const expiryD = new Date(readyD);
    expiryD.setMonth(expiryD.getMonth() + soapExpiryMonths);

    const batch = {
      name: batchName || `${selectedRecipe.name} - ${new Date().toLocaleDateString()}`,
      recipeId: selectedRecipe.id,
      packagingSelection: packagingId ? { id: packagingId, unit: packagingItems.find(p => p.id === packagingId)?.unit || 'pcs', quantityPerBar: 1 } : null,
      deductStock,
      recipeName: selectedRecipe.name,
      madeDate: madeDate,
      bars: bars,
      batchMass: selectedRecipe.computedBatchMass || selectedRecipe.batchSize || 0,
      costPerBar: selectedRecipe.totals?.perBar || 0,
      status: 'curing',
      notes: notes ? [{ text: notes, date: new Date().toISOString() }] : [],
      createdAt: new Date().toISOString(),
      // Soap expiry tracking
      soapExpiryMonths: soapExpiryMonths,
      soapExpiryDate: expiryD.toISOString().split('T')[0],
      readyDate: readyD.toISOString().split('T')[0],
    };

    setSaving(true); setError('');
    try { await onCreate(batch); onClose(); }
    catch (failure) {
      const message = failure.message || 'Failed to create batch.';
      // Stock problems block production; recording without stock is always possible.
      setError(deductStock && /inventory|stock|link/i.test(message)
        ? `${message} To log this batch without changing your stock, untick “Deduct ingredients and packaging from my stock”.`
        : message);
    }
    finally { setSaving(false); }
  };

  return (
    <div className="bat-modal-overlay">
      <div className="bat-modal" role="dialog" aria-modal="true" aria-label="Create production batch">
        <h3 style={{ margin: '0 0 20px' }}>Create New Batch</h3>

        <div className="bat-modal-field">
          <label htmlFor="batch-recipe">Recipe *</label>
          <select id="batch-recipe"
            value={selectedRecipe?.id || ''}
            onChange={(e) => {
              const r = recipes.find(r => r.id === e.target.value);
              setSelectedRecipe(r);
              if (r) { setBars(normalizeRecipe(r).bars); setPackagingId(r.packagingSelection?.id || ''); }
            }}
          >
            <option value="">— Select a recipe —</option>
            {recipes.map(r => (
              <option key={r.id} value={r.id}>{r.name || 'Untitled'}</option>
            ))}
          </select>
        </div>

        {error && <div role="alert" style={{ color: '#b91c1c', marginBottom: 12 }}>{error}</div>}
        <div className="bat-modal-field">
          <label>Packaging (one piece per bar)</label>
          <select value={packagingId} onChange={e => setPackagingId(e.target.value)}>
            <option value="">No packaging deducted</option>
            {packagingItems.filter(item => ['pcs', 'pc', 'piece', 'pieces', 'unit', 'units'].includes((item.unit || 'pcs').toLowerCase())).map(item => <option key={item.id} value={item.id}>{item.name} ({item.stock ?? 0} remaining)</option>)}
          </select>
        </div>

        <div className="bat-modal-field">
          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontWeight: 500 }}>
            <input type="checkbox" checked={deductStock} onChange={e => setDeductStock(e.target.checked)} style={{ width: 'auto', marginTop: 3 }} />
            <span>Deduct ingredients and packaging from my stock</span>
          </label>
          <small>{deductStock
            ? 'Uses the recipe’s linked ingredients and the packaging above. If anything is missing or short, nothing is changed and you will be told what to fix.'
            : 'Record only: the batch is logged with the recipe’s saved cost, and your stock levels stay as they are.'}</small>
        </div>

        <div className="bat-modal-field">
          <label>Batch Name</label>
          <input
            type="text"
            value={batchName}
            onChange={(e) => setBatchName(e.target.value)}
            placeholder="e.g., Lavender Batch #3"
          />
        </div>

        <div className="bat-modal-row">
          <div className="bat-modal-field">
            <label>Date Made</label>
            <input
              type="date"
              value={madeDate}
              onChange={(e) => setMadeDate(e.target.value)}
            />
          </div>
          <div className="bat-modal-field">
            <label>Number of Bars</label>
            <input
              type="number"
              value={bars}
              onChange={(e) => setBars(Number(e.target.value))}
              min="1"
            />
          </div>
          <div className="bat-modal-field">
            <label>Shelf Life (months)</label>
            <select
              value={soapExpiryMonths}
              onChange={(e) => setSoapExpiryMonths(Number(e.target.value))}
            >
              <option value={6}>6 months</option>
              <option value={9}>9 months</option>
              <option value={12}>12 months</option>
              <option value={18}>18 months</option>
              <option value={24}>24 months</option>
            </select>
          </div>
        </div>

        <div className="bat-modal-field">
          <label>Initial Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any observations from making this batch..."
            rows={3}
          />
        </div>

        <div className="bat-modal-actions">
          <button className="bat-action-btn primary" disabled={saving} onClick={handleCreate} style={{ flex: 1 }}>
            {saving ? 'Creating...' : 'Create Batch'}
          </button>
          <button className="bat-action-btn secondary" disabled={saving} onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Batches Page Component
function BatchesPage() {
  const [batches, setBatches] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [packagingItems, setPackagingItems] = useState([]);
  const [workflowError, setWorkflowError] = useState('');

  // Load data
  useEffect(() => {
    let unsubBatches = () => {};
    let unsubRecipes = () => {};
    const unsubPackaging = subscribeToUserCollection('packaging', setPackagingItems);
    
    try {
      unsubBatches = subscribeToUserCollection('batches', (data) => {
        // Sort by createdAt descending
        const sorted = data.sort((a, b) => {
          const aDate = a.createdAt?.toDate?.() || new Date(a.createdAt) || new Date(0);
          const bDate = b.createdAt?.toDate?.() || new Date(b.createdAt) || new Date(0);
          return bDate - aDate;
        });
        setBatches(sorted);
        setLoading(false);
      });

      unsubRecipes = subscribeToUserCollection('recipes', (data) => {
        setRecipes(data);
      });
    } catch (err) {
      console.error('Error loading data:', err);
      setLoading(false);
    }

    return () => { unsubBatches(); unsubRecipes(); unsubPackaging(); };
  }, []);

  const handleCreateBatch = batch => createProductionBatch(batch);

  const handleUpdateBatch = async (id, updates) => {
    setWorkflowError('');
    try { await updateUserDoc('batches', id, updates); return true; }
    catch (err) { setWorkflowError(err.message || 'Failed to update batch.'); return false; }
  };

  const handleDeleteBatch = async id => {
    setWorkflowError('');
    try { await deleteUserDoc('batches', id); }
    catch (err) { setWorkflowError(err.message || 'Failed to delete batch.'); }
  };

  // Filter batches
  const filteredBatches = filterStatus === 'all' 
    ? batches 
    : batches.filter(b => b.status === filterStatus);

  // Stats
  const curingCount = batches.filter(b => b.status === 'curing').length;
  const readyCount = batches.filter(b => b.status === 'ready').length;
  const totalBars = batches.reduce((s, b) => s + num(b.bars, 0), 0);

  return (
    <>
      <div className="sl-page batches-page">
        {/* Page Header */}
        <div className="pageheader borderleft">
          <Breadcrumbs items={[
            { label: 'Home', to: '/' },
            { label: 'Batches', to: '/batches' },
            { label: 'My Batches' }
          ]} />
          <div className="pageheading">My Batches</div>
          <div className="pagedescription">Track soap batches through production, curing, and quality testing.</div>
        </div>

        <div className="batches-page-content borderleft">
          {workflowError && <div role="alert" className="mypadding" style={{ color: '#b91c1c' }}>{workflowError}</div>}
          {/* Stats */}
          <section className="mypadding borderbottom">
            <div className="bat-stats">
              <div className="bat-stat-card">
                <div className="value">{batches.length}</div>
                <div className="label">Total Batches</div>
              </div>
              <div className="bat-stat-card">
                <div className="value">{curingCount}</div>
                <div className="label">Curing</div>
              </div>
              <div className="bat-stat-card">
                <div className="value">{readyCount}</div>
                <div className="label">Ready</div>
              </div>
              <div className="bat-stat-card">
                <div className="value">{totalBars}</div>
                <div className="label">Total Bars</div>
              </div>
            </div>
          </section>

          {/* Toolbar */}
          <section className="mypadding borderbottom">
            <div className="bat-toolbar">
              <button className="bat-btn-create" onClick={() => setShowCreateModal(true)}>
                Create New Batch
              </button>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Statuses</option>
                {BATCH_STATUSES.map(s => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            </div>
          </section>

          {/* Batch List */}
          <section className="mypadding">
            {loading ? (
              <div className="bat-empty">Loading batches...</div>
            ) : filteredBatches.length === 0 ? (
              <div className="bat-empty">
                <div className="title">No batches found</div>
                <div className="subtitle">Create your first batch to start tracking!</div>
              </div>
            ) : (
              <div>
                {filteredBatches.map(batch => (
                  <BatchCard
                    key={batch.id}
                    batch={batch}
                    recipes={recipes}
                    onUpdate={handleUpdateBatch}
                    onDelete={handleDeleteBatch}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      <Footer />
      </div>


      {/* Create Modal */}
      {showCreateModal && (
        <CreateBatchModal
            packagingItems={packagingItems}
          recipes={recipes}
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateBatch}
        />
      )}
    </>
  );
}

export default BatchesPage;
